import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { API, withCreds, inputCls, btnPrimary, Field, Spinner, StatusBadge, money, fmtDate, errMsg } from '../../components/admin/AdminUI';
import { ArrowLeft, Mail, MapPin, ShoppingBag, Check } from 'lucide-react';

function Stat({ label, value }) {
  return (
    <div className="admin-card rounded-xl p-4">
      <p className="text-ink/55 text-xs uppercase tracking-widest">{label}</p>
      <p className="text-ink text-xl font-black mt-1">{value}</p>
    </div>
  );
}

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', admin_notes: '', tags: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const apply = (d) => {
    setData(d);
    const c = d.customer;
    setForm({ name: c.name || '', phone: c.phone || '', admin_notes: c.admin_notes || '', tags: (c.tags || []).join(', ') });
  };

  const load = useCallback(async () => {
    apply((await axios.get(`${API}/admin/customers/${id}`, withCreds)).data);
  }, [id]);

  useEffect(() => { load().catch(() => setData({ error: true })); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/customers/${id}`, { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) }, withCreds);
      apply(r.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (e) { alert(errMsg(e)); }
    finally { setSaving(false); }
  };

  if (!data) return <AdminLayout><Spinner /></AdminLayout>;
  if (data.error) return <AdminLayout><p className="text-ink/55">Customer not found.</p></AdminLayout>;

  const { customer: c, orders, addresses } = data;

  return (
    <AdminLayout>
      <Link to="/admin/customers" className="inline-flex items-center gap-1 text-ink/55 hover:text-ink text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Customers</Link>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-2xl font-black text-ink">{c.name || c.email}</h1>
        <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-honey-700 text-sm hover:underline"><Mail className="w-4 h-4" /> {c.email}</a>
        <span className="text-ink/45 text-sm">Customer since {fmtDate(c.created_at)}</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Orders" value={c.order_count} />
        <Stat label="Total spent" value={money(c.total_spent)} />
        <Stat label="Average order" value={money(c.average_order)} />
        <Stat label="Last order" value={fmtDate(c.last_order_at)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 admin-card rounded-xl overflow-x-auto">
          <h2 className="text-ink font-bold flex items-center gap-2 p-5 pb-3"><ShoppingBag className="w-4 h-4 text-honey-700" /> Orders</h2>
          {orders.length === 0 ? <p className="text-ink/45 text-sm px-5 pb-5">No orders yet.</p> : (
            <table className="w-full text-sm">
              <tbody className="divide-y divide-ink/10">
                {orders.map(o => (
                  <tr key={o.id} className="text-ink/75 hover:bg-ink/[0.04]">
                    <td className="p-3 pl-5"><Link to={`/admin/orders/${o.id}`} className="text-honey-700 font-mono text-xs font-bold hover:underline">{o.order_number}</Link></td>
                    <td className="p-3 text-ink/55 text-xs">{fmtDate(o.created_at)}</td>
                    <td className="p-3 text-xs">{o.items?.reduce((n, i) => n + i.quantity, 0)} item(s)</td>
                    <td className="p-3 font-bold text-ink">{money(o.total)}</td>
                    <td className="p-3"><StatusBadge status={o.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="space-y-6">
          <div className="admin-card rounded-xl p-5 space-y-3">
            <h2 className="text-ink font-bold">Profile</h2>
            <Field label="Name"><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
            <Field label="Phone"><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} /></Field>
            <Field label="Tags" hint="Comma separated, e.g. vip, wholesale"><input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} className={inputCls} /></Field>
            <Field label="Notes (staff only)">
              <textarea value={form.admin_notes} onChange={e => setForm({ ...form, admin_notes: e.target.value })} rows={4} className={`${inputCls} resize-y`} />
            </Field>
            <button onClick={save} disabled={saving} className={`${btnPrimary} w-full`}>{saved ? <><Check className="w-4 h-4" /> Saved</> : saving ? 'Saving…' : 'Save'}</button>
          </div>

          <div className="admin-card rounded-xl p-5">
            <h2 className="text-ink font-bold flex items-center gap-2 mb-3"><MapPin className="w-4 h-4 text-honey-700" /> Addresses used</h2>
            {addresses.length === 0 ? <p className="text-ink/45 text-sm">None yet.</p> : (
              <div className="space-y-3">
                {addresses.map((a, i) => (
                  <p key={i} className="text-ink/75 text-sm leading-snug">
                    {a.name}<br />{a.line1}{a.line2 ? `, ${a.line2}` : ''}<br />{a.city}, {a.state} {a.postal_code} {a.country}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
